import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Link from '@/components/atoms/Link/Link';
import { recipientErrorStatuses } from '@/modules/whatsapp/constants';
import { IntegrationType } from '@/shared/types/integration';

import { infobipErrorStatuses, telnyxErrorStatuses } from '../constants';

interface ChannelErrorConfig {
  docsUrl: (code: string) => string;
  errorStatuses?: Record<string, string>;
}

const INTERNAL_ERROR_MESSAGES: Record<string, string> = {
  camp_102: 'campaigns.errors.subscriber_in_active_session',
};

const INTERNAL_ERROR_CODES = new Set(['camp_100', 'camp_101', 'camp_102', 'bro_100', 'bro_101']);

const WHATSAPP_131026_URL =
  'https://docs.moveo.ai/docs/campaigns#common-issue-error-131026---message-undeliverable';

const defaultConfig: ChannelErrorConfig = {
  docsUrl: () => 'https://www.infobip.com/docs/essentials/response-status-and-error-codes',
  errorStatuses: infobipErrorStatuses,
};

const channelErrorConfig: Record<string, ChannelErrorConfig> = {
  whatsapp: {
    docsUrl: (code) =>
      code === '131026'
        ? WHATSAPP_131026_URL
        : 'https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/',
    errorStatuses: recipientErrorStatuses,
  },
  'infobip-sms': defaultConfig,
  'infobip-rcs': defaultConfig,
  'twilio-voice': { docsUrl: (code) => `https://www.twilio.com/docs/api/errors/${code}` },
  'telnyx-voice': {
    docsUrl: () => 'https://developers.telnyx.com/development/api-fundamentals/api-errors',
    errorStatuses: telnyxErrorStatuses,
  },
};

interface ChannelErrorTooltipProps {
  channel?: IntegrationType;
  status_code: string;
  children: ReactNode;
}

export const ChannelErrorTooltip = ({
  channel,
  status_code,
  children,
}: ChannelErrorTooltipProps) => {
  const { t } = useTranslation();

  if (INTERNAL_ERROR_CODES.has(status_code)) {
    const messageKey = INTERNAL_ERROR_MESSAGES[status_code] ?? 'common.something_wrong';
    return (
      <InfoTooltip content={t(messageKey)} size="auto" delayDuration={100} side="top">
        {children}
      </InfoTooltip>
    );
  }

  const config = channelErrorConfig[channel ?? ''];

  if (!config) {
    return (
      <InfoTooltip
        content={`${t('common.something_wrong')} (#${status_code})`}
        size="auto"
        delayDuration={100}
        side="top"
      >
        {children}
      </InfoTooltip>
    );
  }

  const docsUrl = config.docsUrl(status_code);
  const knownError = config.errorStatuses?.[status_code];
  const message = knownError ? t(knownError) : t('common.something_wrong');

  const content = (
    <>
      {message} (#{status_code}){' '}
      <Link href={docsUrl} external>
        {t('common.learn_more')}
      </Link>
    </>
  );

  return (
    <InfoTooltip content={content} size="auto" delayDuration={100} side="top">
      {children}
    </InfoTooltip>
  );
};
